import ScoopIco from "../../assets/chatbot_scoop.svg";
import CopyIco from "../../assets/copy_ico.svg";
import RefreshIco from "../../assets/refresh_ico.svg";
import InfoWhiteIco from "../../assets/Info_white.svg";
import ReadCheckIco from "../../assets/careerBuddy/ic_read_check.svg";
import React from "react";
import { useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useDispatch, useSelector } from "react-redux";
import { initGetRecommendationsQues } from "../../redux/actions";
import ErrorMsg from "../ErrorMsg";
import {
  clearMsgCompanions,
  sendMsgCompanions,
  updateChatFeedback,
} from "../../redux/actions/aiCompanions";
import LoaderTyping from "../Loader/loaderTyping";
import Loader from "../Loader/loaderSpin";
import { useEffect } from "react";
import { formatText, isHTML, isIOS, useIsMobile } from "../../common/utils";
import { buildChatApiHistory } from "../../common/chatHistory";
import ChatInput from "./ChatInput";
import { ThumbsIco } from "../icons";

const careerBuddyMarkdownComponents = {
  a: ({ node, ...props }) => (
    <a
      {...props}
      className="career-buddy-link"
      target="_blank"
      rel="noopener noreferrer"
    />
  ),
};

/** Markdown on every stream update so completed syntax (e.g. `**bold**`) formats immediately. */
function CareerBuddyMessage({ text, streaming }) {
  const body = text ?? "";
  return (
    <div
      className={`career-buddy-streaming${
        streaming ? " career-buddy-streaming--live" : ""
      }`}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={careerBuddyMarkdownComponents}>
        {`**Career Buddy>** ${body}`}
      </ReactMarkdown>
    </div>
  );
}

const aiImpact = {
  M: {
    title: "Moderate AI Impact",
    short_title: "Moderate",
    description:
      "AI may change how some tasks are done, but people will continue to play a key role.",
    img: require("../../assets/careerBuddy/ic_moderate_ai_impact.png"),
    color: "#F2C94C",
    backgroundColor: "rgba(237, 194, 67, 0.4)",
  },
  H: {
    title: "High AI Impact",
    short_title: "High",
    description:
      "Many tasks in this field may be automated. New skills may be needed to stay ahead.",
    img: require("../../assets/careerBuddy/ic_high_ai_impact.png"),
    color: "#ED6B43",
    backgroundColor: "rgba(237, 107, 67, 0.4)",
  },
  L: {
    title: "Low AI Impact",
    short_title: "Low",
    description:
      "In the near future, AI isn’t expected to change this career much. Human skills still matter most.",
    img: require("../../assets/careerBuddy/ic_low_ai_impact.png"),
    color: "#07D3C4",
    backgroundColor: "rgba(7, 211, 196, 0.4)",
  },
};
export default function Chatbot({
  question,
  isMoreRecommendations,
  agentId,
  profile,
  maskTitle,
  impact,
}) {
  const isMobile = useIsMobile();
  const [feedbackReaction, setFeedbackReaction] = useState(null);

  const aiImpactId = question?.filter((item) => item.ordering === 5)[0]?.id;
  const dispatch = useDispatch();
  const { quesAnw, isLoading } = useSelector((state) => state.scenariosReducer);
  const isLoadingRef = useRef(isLoading);
  const { chatHistory, isTypingAi, isChatLoad } = useSelector(
    (state) => state.aiCompanionsReducer,
  );

  const lastAssistantMessageId = useMemo(() => {
    for (let i = chatHistory.length - 1; i >= 0; i--) {
      const m = chatHistory[i];
      if (m?.type === "assistant" && m?.message_id) {
        return m.message_id;
      }
    }
    return null;
  }, [chatHistory]);

  const [copyMessage, setCopyMessage] = useState("");
  const [chatbotCopyMessage, setChabotCopyMessage] = useState("");
  const [errorMsgLocal, setErrorMsg] = useState("");
  const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);
  const [displayedMessages, setDisplayedMessages] = useState([]);
  const [typingIndex, setTypingIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);

  /** Keep live SSE text in sync (same pattern as AI Companions detail page). */
  const displayForRender = useMemo(() => {
    const last = chatHistory[chatHistory.length - 1];
    if (last?.type === "assistant") {
      const out = [...displayedMessages];
      if (out.length && out[out.length - 1]?.type === "assistant") {
        out[out.length - 1] = { ...out[out.length - 1], ...last };
        return out;
      }
      if (out.length && out[out.length - 1]?.type === "user") {
        out.push(last);
        return out;
      }
    }
    return displayedMessages;
  }, [chatHistory, displayedMessages]);

  const [countRefresh, setCountRefresh] = useState({});
  const [openAnswerId, setOpenAnswerId] = useState(false);
  const [typingAnswer, setTypingAnswer] = useState("");
  const textareaRef = useRef(null);
  const [text, setText] = useState("");
  const chatContainerRef = useRef(null);
  const typingChatBotMsg = useRef(false);
  const typingIntervalRef = useRef(null);
  /** Legacy non-stream API uses character-by-stream animation; SSE stream must merge live text instead. */
  const assistantTypewriterIntervalRef = useRef(null);
  const [isMobileImpact, setIsMobileImpact] = useState(true);
  useEffect(() => {
    dispatch(clearMsgCompanions());
    if (isMoreRecommendations) {
      setOpenAnswerId(false);
    }
  }, [isMoreRecommendations]);

  useEffect(() => {
    if (text) {
      handleToggleAnswer();
    }
  }, [text]);
  useEffect(() => {
    handleClearChat();
    return () => {
      handleClearChat();
    };
  }, []);
  const handleClearChat = async () => {
    if (assistantTypewriterIntervalRef.current) {
      clearInterval(assistantTypewriterIntervalRef.current);
      assistantTypewriterIntervalRef.current = null;
    }
    await dispatch(clearMsgCompanions());
    setDisplayedMessages([]);
    setIsTyping(false);
    setTypingIndex(0);
    typingChatBotMsg.current = true;
  };
  useEffect(() => {
    if (
      !isTyping &&
      typingChatBotMsg.current &&
      typingIndex < chatHistory.length
    ) {
      const message = chatHistory[typingIndex];
      if (message?.type === "assistant") {
        if (message.streaming === true) {
          return;
        }
        if (message.streaming === false) {
          setDisplayedMessages((prev) => {
            const next = [...prev];
            if (next.length && next[next.length - 1]?.type === "assistant") {
              next[next.length - 1] = {
                ...message,
                text: message.text ?? "",
                streaming: false,
              };
              return next;
            }
            return [
              ...next,
              {
                ...message,
                text: message.text ?? "",
                streaming: false,
              },
            ];
          });
          setTypingIndex((prev) => prev + 1);
          setErrorMsg("");
          return;
        }
        // Legacy non-stream: typewriter (only when final text is present)
        const fullText = message.text ?? "";
        if (!fullText.length) {
          return;
        }
        const useTypingAnimation =
          fullText.length < 800 &&
          !fullText.includes("|") &&
          (fullText.match(/\n/g) || []).length < 8;
        if (!useTypingAnimation) {
          setDisplayedMessages((prev) => [...prev, { ...message, text: fullText }]);
          setTypingIndex((prev) => prev + 1);
          setErrorMsg("");
          setIsTyping(false);
          return;
        }
        if (assistantTypewriterIntervalRef.current) {
          clearInterval(assistantTypewriterIntervalRef.current);
          assistantTypewriterIntervalRef.current = null;
        }
        setIsTyping(true);
        let i = 0;
        let typedMessage = "";
        setDisplayedMessages((prev) => [...prev, { ...message, text: "" }]);
        assistantTypewriterIntervalRef.current = setInterval(() => {
          typedMessage += fullText[i] ?? "";
          setDisplayedMessages((prev) => {
            const updatedMessages = [...prev];
            updatedMessages[updatedMessages.length - 1] = {
              ...message,
              text: typedMessage,
            };
            return updatedMessages;
          });
          i++;
          if (i >= fullText.length) {
            if (assistantTypewriterIntervalRef.current) {
              clearInterval(assistantTypewriterIntervalRef.current);
              assistantTypewriterIntervalRef.current = null;
            }
            setErrorMsg("");
            setTypingIndex((prev) => prev + 1);
            setIsTyping(false);
          }
        }, 8);
      } else {
        setDisplayedMessages((prev) => [...prev, message]);
        setTypingIndex((prev) => prev + 1);
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop =
            chatContainerRef.current.scrollHeight;
        }
      }
      textareaRef.current?.focus();
    }
  }, [chatHistory, typingIndex, isTyping]);
  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (!chatContainer) return;
    requestAnimationFrame(() => {
      chatContainer.scrollTop = chatContainer.scrollHeight;
    });
  }, [displayForRender, isTypingAi]);

  const handleSend = (e) => {
    typingChatBotMsg.current = true;
    e.preventDefault();
    if (text.trim().length > 5000) {
      setErrorMsg("Message trimmed to fit the 5000-character limit.");
    }
    if (text.trim().length) {
      setText("");

      textareaRef.current.style.height = "45px"; // Reset height

      const params = {
        id: agentId,
        text: text.length > 5000 ? text.substring(0, 5000) : text,
        role: "user",
        first_msg: getFirstMsg(),
        history: buildChatApiHistory(chatHistory),
      };
      dispatch(sendMsgCompanions(params));
    }
  };
  const getFirstMsg = () => {
    return `You’re chatting with ${profile?.first_name}, a ${
      profile?.age
    }-year-old from ${profile?.city ? ", " + profile?.city : ""}${
      profile?.state ? ", " + profile?.state : ""
    } who is interested to learn more about ${maskTitle} career field`;
  };
  const copyToClipboard = async (htmlElement, index) => {
    try {
      setCopiedMessageIndex(index); // Store the copied message index
      const divElement = document.createElement("div");
      divElement.innerHTML = htmlElement;
      const text = divElement.innerText || divElement.textContent;
      await navigator.clipboard.writeText(text);
      setCopyMessage("Copied successfully!");
      // Hide the message after 3 seconds (optional)
      setTimeout(() => {
        setCopyMessage("");
      }, 3000);
    } catch (err) {
      //   setMessage("Failed to copy text.");
      console.error("Error copying text: ", err);
    }
  };

  useEffect(() => {
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
    }
    if (openAnswerId) {
      const answer =
        quesAnw[openAnswerId]?.answer ||
        question.find((q) => q.id === openAnswerId)?.answer?.answer;
      if (answer) {
        simulateTyping(answer); // Trigger typing effect once the answer is ready
      }
    }
    return () => {
      // Clear the interval when the component unmounts or state changes
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
      }
    };
  }, [quesAnw, openAnswerId]);

  const handleToggleAnswer = (id) => {
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
      setTypingAnswer("");
      setCopyMessage("");
    }
    if (openAnswerId === id) {
      setTypingAnswer(false);
      setOpenAnswerId(null);
      return;
    }
    // If clicking a different button while typing, cancel current typing
    setOpenAnswerId(openAnswerId === id ? null : id);
  };

  const handleRefreshAnw = (id) => {
    setCountRefresh({
      ...countRefresh,
      [id]: (countRefresh[id] || 1) + 1,
    });
    dispatch(initGetRecommendationsQues(id));
  };
  const simulateTyping = (htmlString) => {
    if (!htmlString) return;
    const text = htmlString.replace(/<\/?[^>]+(>|$)/g, "");
    setTypingAnswer(text.charAt(0));
    let index = 0;
    typingIntervalRef.current = setInterval(() => {
      if (index < text.length) {
        setTypingAnswer((prev) => prev + text.charAt(index));
        index++;
      } else {
        clearInterval(typingIntervalRef.current);
      }
    }, 8); // Adjust speed as needed
  };
  const copyFullChatToClipboard = async () => {
    try {
      setCopiedMessageIndex("chatbot");
      const chatText = displayedMessages
        .map((msg) =>
          msg.type === "user"
            ? `You> ${msg.text}`
            : `Career Buddy> ${msg.text}`,
        )
        .join("\n");

      await navigator.clipboard.writeText(chatText);
      setChabotCopyMessage("Full chat copied successfully!");
      setTimeout(() => setCopiedMessageIndex(null), 3000);
    } catch (err) {
      console.error("Failed to copy chat: ", err);
    }
  };

  if (!question?.length) {
    return null;
  }
  const CopyButtonWithMessage = ({ message, disabled, onCopy, index }) => {
    return (
      <>
        {message && (
          <div className="CareerCont__chatbot__des__message">{message}</div>
        )}
        <button disabled={disabled} type="button" onClick={onCopy}>
          {copiedMessageIndex === index ? (
            <img src={ReadCheckIco} alt="copy" />
          ) : (
            <img src={CopyIco} alt="copy" />
          )}
        </button>
      </>
    );
  };
  return (
    <div className="CareerCont__chatbot">
      <div className="CareerCont__chatbot__aiCont">
        <div className="career-impact-mobile">
          <ImpactCard
            onClick={() => {
              handleToggleAnswer(aiImpactId);
              setIsMobileImpact(!isMobileImpact);
            }}
            onClickExpand={() => {
              handleToggleAnswer(null);
              setIsMobileImpact(!isMobileImpact);
            }}
            impact={impact}
            openAnswerId={openAnswerId ? true : isMobileImpact}
          />
        </div>
        <div className="d-flex justify-between align-items-center">
          <h3>
            Get the Scoop on This Career <img src={ScoopIco} alt="Scoop" />
          </h3>
          <div className="CareerCont__chatbot__aiCont__impact">
            <ImpactCardTooltip
              openAnswerId={openAnswerId}
              impact={impact}
              onClickExpand={() => handleToggleAnswer(null)}
            />
          </div>
        </div>
        <div className="CareerCont__chatbot__aiCont__tabs">
          <ul
            className="CareerCont__chatbot__list"
            style={{
              width: isMobile || openAnswerId ? "100%" : "calc(100% - 315px)",
            }}
          >
            {question?.map((items, key) => (
              <li key={items.id}>
                <button
                  type="button"
                  onClick={() => {
                    if (!isIOS()) {
                      handleToggleAnswer(items.id);
                      setIsMobileImpact(!isMobileImpact);
                    }
                  }}
                  onTouchStart={() => {
                    if (isIOS()) {
                      handleToggleAnswer(items.id);
                      setIsMobileImpact(!isMobileImpact);
                    }
                  }}
                  className={`badge ${openAnswerId === items.id ? "active" : ""}`}
                >
                  {items.question}
                </button>
                {openAnswerId === items.id && (
                  <div className="CareerCont__chatbot__des ">
                    <span>{countRefresh[items.id] || 1}/5 scoops</span>
                    {isLoadingRef.current ? (
                      <Loader />
                    ) : isHTML(items.answer?.answer) ? (
                      <div
                        dangerouslySetInnerHTML={{ __html: typingAnswer }}
                      ></div>
                    ) : (
                      <p>{formatText(typingAnswer)}</p>
                    )}
                    <div className="CareerCont__chatbot__des__btns">
                      <CopyButtonWithMessage
                        message={copyMessage}
                        disabled={isLoading.current}
                        index={key}
                        onCopy={() =>
                          copyToClipboard(
                            quesAnw[items.id]?.answer || items.answer?.answer,
                            key,
                          )
                        }
                      />

                      <button
                        disabled={
                          isLoading.current || countRefresh[items.id] === 5
                        }
                        className={
                          countRefresh[items.id] === 5 ? "btn-disabled" : ""
                        }
                        type="button"
                        onClick={() => handleRefreshAnw(items.id)}
                      >
                        <img src={RefreshIco} alt="refresh" />
                      </button>
                      <button
                        disabled={isLoading.current}
                        type="button"
                        onClick={() => handleToggleAnswer(null)}
                      >
                        Back
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
          {!openAnswerId && (
            <ImpactCard
              onClick={() => handleToggleAnswer(aiImpactId)}
              impact={impact}
            />
          )}
        </div>
      </div>
      {agentId && (
        <>
          {displayForRender.length > 0 && (
            <div
              className="CareerCont__chatbot__des CareerCont__chatbot__scroll"
              ref={chatContainerRef}
            >
              {displayForRender.length ? (
                displayForRender.map((item, key) =>
                  item.type === "user" ? (
                    <div key={key} className="CareerCont__chatbot__des__user">
                      <strong>You&gt;</strong> {item.text}
                    </div>
                  ) : item.type === "assistant" &&
                    !String(item.text ?? "").trim() ? null : (
                    <p key={key} className="CareerCont__chatbot__des__bot">
                      <CareerBuddyMessage
                        text={item.text}
                        streaming={Boolean(item.streaming)}
                      />
                    </p>
                  )
                )
              ) : (
                <p className="text-center">Chat not started yet</p>
              )}
              {isTypingAi ? (
                <div
                  className="aiCompanions__chat__support CareerCont__chatbot__des__typing"
                  style={{ backgroundColor: "#20528B", borderRadius: "8px" }}
                >
                  <div className="message d-inline-flex">
                    <LoaderTyping />
                  </div>
                </div>
              ) : null}
              <div className="CareerCont__chatbot__des__btns CareerCont__chatbot__des__space">
                <CopyButtonWithMessage
                  message={""}
                  disabled={isTypingAi}
                  onCopy={copyFullChatToClipboard}
                  index={"chatbot"}
                />
                <button
                  type="button"
                  disabled={!lastAssistantMessageId || isTypingAi}
                  className={`thumbs-up ${feedbackReaction === "up" ? "thumbs-active" : ""}`}
                  onClick={() => {
                    if (!lastAssistantMessageId) return;
                    setFeedbackReaction("up");
                    dispatch(
                      updateChatFeedback({
                        agentId: agentId,
                        message_id: lastAssistantMessageId,
                        feedback: "up",
                      }),
                    );
                  }}
                  title="Thumbs up"
                  aria-label="Good response"
                >
                  <ThumbsIco />
                </button>
                <button
                  type="button"
                  disabled={!lastAssistantMessageId || isTypingAi}
                  className={`${feedbackReaction === "down" ? "thumbs-active" : ""}`}
                  onClick={() => {
                    if (!lastAssistantMessageId) return;
                    setFeedbackReaction("down");
                    dispatch(
                      updateChatFeedback({
                        agentId: agentId,
                        message_id: lastAssistantMessageId,
                        feedback: "down",
                      }),
                    );
                  }}
                  title="Thumbs down"
                  aria-label="Poor response"
                >
                  <ThumbsIco />
                </button>
              </div>
            </div>
          )}

          <ErrorMsg errorMsg={errorMsgLocal} />
          <ChatInput
            text={text}
            setText={setText}
            isTyping={isTyping}
            isTypingAi={isTypingAi}
            onSend={handleSend}
            textareaRef={textareaRef}
            placeholder="Ask me anything about this career..."
          />
        </>
      )}
      {!agentId && (
        <>
          <form className="CareerCont__chatbot__input">
            <input
              type="text"
              placeholder="Select an option above..."
              disabled
            />
            <div className="tooltip-container">
              <button type="button">
                <img src={InfoWhiteIco} alt="info_ico" />
              </button>
              <span className="tooltip-text CareerCont__chatbot__des__message">
                AI Career Buddy is only available with certain subscriptions
              </span>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
const ImpactCard = ({ impact, onClick, onClickExpand, openAnswerId }) => {
  const impactData = aiImpact[impact];
  return (
    <div className="CareerCont__chatbot__aiCont__impact">
      <div className="career-impact-mobile">
        <ImpactCardTooltip
          onClickExpand={onClickExpand}
          openAnswerId={openAnswerId}
          impact={impact}
        />
      </div>
      {!openAnswerId && (
        <div
          className="CareerCont__chatbot__aiCont__impact__card"
          style={{
            borderColor: impactData.color,
            backgroundColor: impactData.backgroundColor,
          }}
        >
          <h3>
            <img src={impactData.img} height="36px" alt={impactData.title} />{" "}
            {impactData.title}
          </h3>
          <p>{impactData.description}</p>
          <button
            type="button"
            onClick={onClick}
            style={{ backgroundColor: impactData.color }}
          >
            Explore the AI impact
          </button>
        </div>
      )}
    </div>
  );
};
const ImpactCardTooltip = ({ openAnswerId, impact, onClickExpand }) => {
  const impactData = aiImpact[impact];
  return (
    <p className="note">
      <span className="note-para">
        Curious how AI might change this field? Our forecast:
      </span>
      <div className="tooltip-container">
        <button type="button">
          <img src={InfoWhiteIco} alt="info_ico" />
        </button>
        <span className="tooltip-text CareerCont__chatbot__des__message">
          This forecast is based on current trends — and may shift as technology
          evolves.
        </span>
      </div>
      {openAnswerId && (
        <button
          onClick={onClickExpand}
          className="CareerCont__chatbot__aiCont__impact__cardSm"
          style={{
            borderColor: impactData.color,
            backgroundColor: impactData.backgroundColor,
          }}
        >
          <p>
            <img src={impactData.img} height="16px" alt={impactData.title} />{" "}
            {impactData.short_title}
          </p>
        </button>
      )}
    </p>
  );
};
